import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAcceptInvitation } from '../hooks/useTeamMembers'
import { useToast } from '../hooks/useToast'

/**
 * Map the raw RPC error message to something we'd show in a UI. The RPC
 * raises `raise exception 'invitation_expired'` etc, and supabase-js
 * surfaces the literal text as Error.message.
 */
function friendlyInviteError(raw: string): string {
  if (raw.includes('invitation_expired')) return 'This invitation has expired. Ask the inviter to send a new one.'
  if (raw.includes('invitation_revoked')) return 'This invitation was revoked.'
  if (raw.includes('invitation_not_found')) return 'Invitation link is invalid.'
  if (raw.includes('invitation_email_mismatch')) return 'This invitation was sent to a different email address. Sign in with the correct account.'
  if (raw.includes('not_authenticated')) return 'Please sign in to accept this invitation.'
  return raw
}

/**
 * Mounted inside the authed branch of the app. On mount, checks the URL for
 * `?invite=<token>`. If present, calls the `accept_team_invitation` RPC and
 * strips the param from the URL so a refresh doesn't retry.
 *
 * We keep this a sibling of `<Shell>` (rather than baking it into `App`) so
 * the auth gate has already resolved — `accept_team_invitation` requires a
 * logged-in Supabase session and would 401 otherwise. If an un-authed user
 * hits an invite URL we leave `?invite` in place; the login flow preserves
 * the current URL, so after they sign in this component re-mounts and the
 * token is still there to accept.
 *
 * Renders a small toast on success/error. No other UI — it's fire-and-forget
 * from the user's perspective.
 */
export function InviteHandler() {
  const accept = useAcceptInvitation()
  const { toast, show } = useToast({ durationMs: 4000 })
  const location = useLocation()
  const navigate = useNavigate()
  // Guard against React 18 StrictMode double-invoking the effect in dev —
  // without this we'd hit the RPC twice on mount.
  const ranRef = useRef(false)

  useEffect(() => {
    if (ranRef.current) return
    // Read the token from the router-reported location so we're consistent
    // with whatever `useLocation()` sees. Falls back to window.location only
    // for the very first render before the BrowserRouter has settled.
    const params = new URLSearchParams(location.search)
    const token = params.get('invite')
    if (!token) return
    ranRef.current = true

    // Strip the param immediately — even if the RPC fails we don't want the
    // token sitting in the address bar where it could leak via clipboard /
    // bookmarks / referer. Navigate via react-router (replace) so
    // `useLocation()` observers upstream see the cleaned URL; previously
    // this used `window.history.replaceState`, which bypassed the router
    // (review HIGH #7 / LOW InviteHandler).
    params.delete('invite')
    const nextSearch = params.toString()
    const cleanedUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`
    navigate(cleanedUrl, { replace: true })

    accept.mutate(token, {
      onSuccess: (result) => {
        if (result.already_member) {
          show(`You're already in ${result.team_name}`)
        } else {
          show(`Joined ${result.team_name} 🎉`)
        }
      },
      onError: (err) => {
        const raw = (err as Error).message || 'Invitation could not be accepted'
        // Translate known RPC error messages (raised by the SECURITY DEFINER
        // function) into friendlier copy. Anything unrecognised falls through
        // to the raw message so debugging isn't hidden.
        const friendly = friendlyInviteError(raw)
        show(`Invite failed: ${friendly}`, 6000)
      },
    })
  }, [accept, show, location.search, location.pathname, location.hash, navigate])

  if (!toast) return null
  return (
    <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-zinc-900 px-4 py-2.5 shadow-lg">
      <span className="text-xs text-white">{toast}</span>
    </div>
  )
}
